import { useContext, useEffect, useRef, useState } from "react";
import { Box, Link, Typography } from "@mui/material";
import { useLocation, useSearchParams } from "react-router-dom";
import { AuthContext } from "src/utils/AuthContext.js";
import { customEvent } from "src/utils/gtag.js";
import { withoutEmailLinkParams } from "src/utils/firebaseSignIn.js";
import StanzaIcon from "src/assets/svg/stanza-small-icon.js";
import { LoadingButton } from "../LoadingButton.js";
import ResponsiveDialog from "./ResponsiveDialog.js";

/**
 * Tracks a step of the email-link confirmation. `url` omits the query so the
 * address and one-time code never reach GA4.
 *
 * @param {"shown" | "clicked" | "dismissed"} step
 */
const trackEmailLinkPrompt = (step) =>
  customEvent({
    name: `user_signin_email_link_prompt_${step}`,
    category: "Authorization",
    label: `Email Link Prompt ${step}`,
    data: {
      provider: "email",
      url: `${window.location.origin}${window.location.pathname}`,
    },
  });

/**
 * Asks the user to confirm an email sign-in link before redeeming it, so link
 * scanners that open the page can't spend the one-time code.
 */
export default function EmailLinkConfirmDialog() {
  const { user, pendingEmailLink, confirmEmailLinkSignIn, dismissEmailLink } =
    useContext(AuthContext) ?? {};
  const [, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [isConfirming, setIsConfirming] = useState(false);
  const shownRef = useRef(false);

  const email = pendingEmailLink?.email;
  const isAlreadySignedIn =
    Boolean(email) && user?.email?.toLowerCase() === email.toLowerCase();
  const open = Boolean(email) && !isAlreadySignedIn;

  /** Drops the link params from the URL, keeping the rest of the query. */
  const clearLinkFromUrl = () =>
    setSearchParams(
      withoutEmailLinkParams(window.location.search, window.location.pathname),
      { replace: true, state: location.state }
    );

  useEffect(() => {
    if (isAlreadySignedIn) {
      clearLinkFromUrl();
      dismissEmailLink();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAlreadySignedIn]);

  useEffect(() => {
    if (open && !shownRef.current) {
      shownRef.current = true;
      trackEmailLinkPrompt("shown");
    }
  }, [open]);

  const handleContinue = async () => {
    if (isConfirming) return;
    setIsConfirming(true);
    trackEmailLinkPrompt("clicked");
    // Clean first: the link was captured on load, and a reload must not re-offer a spent code.
    clearLinkFromUrl();
    try {
      await confirmEmailLinkSignIn();
    } finally {
      setIsConfirming(false);
    }
  };

  const handleDismiss = () => {
    if (isConfirming) return;
    trackEmailLinkPrompt("dismissed");
    clearLinkFromUrl();
    dismissEmailLink();
  };

  if (!open) return null;

  return (
    <ResponsiveDialog
      open={open}
      handleClose={handleDismiss}
      data-testid="email-link-confirm-dialog"
    >
      <Box sx={{ textAlign: "center", px: { xs: 1, md: 3 }, pb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2, lineHeight: 0 }}>
          <StanzaIcon style={{ width: 36, height: 40 }} />
        </Box>
        <Typography variant="h2" sx={{ mb: 1.5 }}>
          Finish signing in
        </Typography>
        <Typography
          variant="subtitle1"
          color="text.secondary"
          sx={{ mb: 3, overflowWrap: "anywhere" }}
        >
          Continue as <b style={{ color: "#000000" }}>{email}</b>
        </Typography>
        <LoadingButton
          variant="contained"
          size="grown"
          data-testid="email-link-confirm-continue"
          sx={{ width: "100%" }}
          loading={isConfirming}
          onClick={handleContinue}
        >
          Continue
        </LoadingButton>
        <Box sx={{ mt: 2 }}>
          <Link
            component="button"
            type="button"
            variant="body2"
            data-testid="email-link-confirm-dismiss"
            onClick={handleDismiss}
            disabled={isConfirming}
          >
            Not you? Sign in another way
          </Link>
        </Box>
      </Box>
    </ResponsiveDialog>
  );
}
